import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import surveyService from "../../Services/surveyService";
import CreateSurvey from "./CreateSurvey";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  LineChart,
  Line,
  CartesianGrid,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import {
  faClipboardList,
  faCalendarAlt,
  faCheckCircle,
  faPauseCircle,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";

export default function SurveyList() {
  const [UserDetails, setUserDetails] = useState(null);
  const [surveyDetails, setSurveyDetails] = useState([]);
  const [activeTab, setActiveTab] = useState("Active");
  const [selectedDays, setSelectedDays] = useState("Last 3 Days");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [surveyData, setSurveyData] = useState([]);
  const [showCreateSurvey, setShowCreateSurvey] = useState(false);
  const [chartType, setChartType] = useState("line");

  const navigate = useNavigate();
  const location = useLocation();

  const surveySectionRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("JWTToken");
    const userId = localStorage.getItem("userID");

    if (!token || !userId) {
      navigate("/Login", { replace: true });
      return;
    }

    let isMounted = true;

    const fetchSurveys = async () => {
      try {
        setLoading(true);
        const response = await surveyService.getAllSurveysByUser({
          token,
          userId: parseInt(userId, 10),
        });

        if (isMounted && response?.status === "Success") {
          console.log("Surveys Details:", response);
          const surveys = response.surveys.map((s) => {
            let statusFlag = 0;
            switch (s.displayStatus) {
              case "Active":
                statusFlag = 1;
                break;
              case "Completed":
                statusFlag = 2;
                break;
              case "Inactive":
                statusFlag = 3;
                break;
              case "Scheduled":
                statusFlag = 4;
                break;
              case "Draft":
                statusFlag = 0;
                break;
              default:
                statusFlag = 0;
            }
            return { ...s, statusFlag };
          });

          setSurveyDetails(surveys);
        } else {
          console.error("Failed to fetch surveys:", response?.message);
          setSurveyDetails([]);
        }
      } catch (err) {
        console.error("Error fetching surveys:", err);
        setSurveyDetails([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSurveys();
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (location.state?.defaultTab) {
      setActiveTab(location.state.defaultTab);
    }
  }, [location.state]);

  const stripHtml = (html = "") => {
  return html
    .replace(/<[^>]*>/g, "")   // remove tags
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};


  const categorizedSurveys = {
    Active: surveyDetails?.filter((s) => s.statusFlag === 1) || [],
    Scheduled: surveyDetails?.filter((s) => s.statusFlag === 4) || [],
    Completed: surveyDetails?.filter((s) => s.statusFlag === 2) || [],
    Draft: surveyDetails?.filter((s) => s.statusFlag === 0) || [],
    Inactive: surveyDetails?.filter((s) => s.statusFlag === 3) || [],
  };

  const filteredSurveys = categorizedSurveys[activeTab]?.filter((survey) =>
    survey?.assessmentName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const SurveyCard = ({ survey }) => (
    <div
      onClick={() =>
        navigate(`/add-questions/${survey.assessmentUID}`, {
          state: { surveyData: survey },
        })
      }
      className="cursor-pointer bg-white/80 backdrop-blur-lg border rounded-xl shadow-md p-4 w-48 h-56 hover:shadow-lg transition flex flex-col justify-between"
    >
      <p className="text-xs text-gray-500">
        {survey?.startDateTime ? survey.startDateTime.split("T")[0] : "N/A"}
      </p>

      <h6 className="text-sm font-bold line-clamp-2">
        {survey?.assessmentName || "Untitled Survey"}
      </h6>
      <p className="text-xs text-gray-600">
        {truncateWords(survey?.description, 15) || "-"}
      </p>
      <p className="text-xs text-gray-600">
        Time: {survey?.totalTimeForSurvey || "N/A"}
      </p>
    </div>
  );

  const truncateWords = (htmlText, wordLimit) => {
  if (!htmlText) return "";

  const plainText = stripHtml(htmlText);
  const words = plainText.split(" ");

  if (words.length <= wordLimit) return plainText;
  return words.slice(0, wordLimit).join(" ") + "...";
};


  const handleCardClick = (tab) => {
    setActiveTab(tab);
    surveySectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const pieData = [
    { name: "Active", value: categorizedSurveys.Active?.length || 0 },
    { name: "Completed", value: categorizedSurveys.Completed?.length || 0 },
    { name: "Draft", value: categorizedSurveys.Draft?.length || 0 },
  ];

  const COLORS = ["#3BB290", "#FF7F0E", "#E74C3C"];

  return (
    <div className="min-h-screen bg-gray-50 py-10 Survey-list-wrapper">
      <div className="max-w-7xl mx-auto px-6">
        {showCreateSurvey ? (
          <CreateSurvey />
        ) : (
          <>
            {/* Dashboard Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6 mb-8">
              {[
                {
                  label: "Active",
                  value: categorizedSurveys.Active?.length || 0,
                  icon: faClipboardList,
                },
                {
                  label: "Scheduled",
                  value: categorizedSurveys.Scheduled?.length || 0,
                  icon: faCalendarAlt,
                },
                {
                  label: "Completed",
                  value: categorizedSurveys.Completed?.length || 0,
                  icon: faCheckCircle,
                },
                {
                  label: "Inactive",
                  value: categorizedSurveys.Inactive?.length || 0,
                  icon: faPauseCircle,
                },
                {
                  label: "Draft",
                  value: categorizedSurveys.Draft?.length || 0,
                  icon: faFileAlt,
                },
              ].map((stat, i) => (
                <div
                  key={i}
                  onClick={() => handleCardClick(stat.label)}
                  className={`cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 p-5 rounded-2xl shadow-md flex justify-between items-center min-w-[200px] hover:shadow-xl hover:scale-105 transition-transform ${
                    activeTab === stat.label ? "ring-2 ring-[#3bb290]" : ""
                  }`}
                >
                  <div className="flex items-center justify-center w-20 h-20 rounded-full bg-[#3bb290]/10 text-[#3bb290] shadow-sm">
                    <FontAwesomeIcon icon={stat.icon} size="2xl" />
                  </div>

                  <div className="flex flex-col text-right">
                    <h5 className="text-sm font-medium text-gray-600">
                      {stat.label}
                    </h5>
                    <p className="text-xl font-bold text-[#3bb290]">
                      {stat.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Chart Switcher */}
              <div className="lg:col-span-2 bg-white/80 backdrop-blur-lg p-6 rounded-xl shadow-md">
                <div className="flex justify-between items-center mb-4">
                  <h5 className="font-semibold text-gray-800">
                    Survey Analysis
                  </h5>
                  <div className="flex items-center gap-2">
                    <select
                      className="border rounded-md px-2 py-1 text-sm"
                      value={selectedDays}
                      onChange={(e) => setSelectedDays(e.target.value)}
                    >
                      {surveyData?.map((d, i) => (
                        <option key={i} value={d?.TimePeriod || ""}>
                          {d?.TimePeriod || "-"}
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={() =>
                        setChartType(chartType === "line" ? "bar" : "line")
                      }
                      className="ml-3 px-3 py-1 text-sm rounded-md bg-[#3bb290] text-white hover:bg-[#339b7e] transition"
                    >
                      {chartType === "line"
                        ? "Switch to Bar"
                        : "Switch to Line"}
                    </button>
                  </div>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                  {chartType === "line" ? (
                    <LineChart data={surveyData || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="TimePeriod" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="SurveyPublished"
                        stroke="#3bb290"
                        strokeWidth={3}
                        dot={{ r: 5, fill: "#3bb290" }}
                        activeDot={{ r: 7 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="ResponseRate"
                        stroke="#ff7300"
                        strokeWidth={3}
                        dot={{ r: 5, fill: "#ff7300" }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  ) : (
                    <BarChart data={surveyData || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="TimePeriod" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="SurveyPublished" fill="#3bb290" />
                      <Bar dataKey="ResponseRate" fill="#ff7300" />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>

              {/* Donut Chart */}
              <div className="bg-white/80 backdrop-blur-lg p-6 rounded-xl shadow-md flex flex-col justify-center items-center">
                <div className="flex justify-between items-center w-full mb-4">
                  <h5 className="font-semibold text-gray-800">
                    Survey Status Distribution
                  </h5>
                </div>
                <ResponsiveContainer width="100%" height={290}>
                  <PieChart>
                    {/* <Pie
                      data={pieData || []}
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={4}
                      dataKey="value"
                    >
                      {pieData?.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie> */}

                    <Pie
                      data={pieData || []}
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={4}
                      dataKey="value"
                      startAngle={270} // top
                      endAngle={-270} // clockwise direction
                    >
                      {pieData?.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Tabs & Search */}
            <div
              ref={surveySectionRef}
              className="flex justify-between items-center mb-6 mt-8"
            >
              <div className="flex gap-6 border-b">
                {["Active", "Scheduled", "Completed", "Inactive", "Draft"].map(
                  (tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`relative pb-2 text-sm font-medium transition-colors ${
                        activeTab === tab
                          ? "text-black"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {tab}
                      {activeTab === tab && (
                        <span className="absolute left-0 bottom-0 w-full h-[2px] bg-[#3bb290] rounded-full"></span>
                      )}
                    </button>
                  )
                )}
              </div>

              <input
                type="text"
                placeholder=" Search surveys..."
                className="border rounded-lg px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3bb290]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Surveys Grid */}
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin h-10 w-10 border-4 border-[#3bb290] border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {/* New Survey Card */}
                <div
                  onClick={() => navigate("/create-survey")}
                  className="cursor-pointer bg-[#3bb290] text-white rounded-xl flex items-center justify-center w-48 h-56 shadow-md hover:shadow-lg transition"
                >
                  <h6 className="font-bold">New Survey</h6>
                </div>

                {filteredSurveys?.length > 0 ? (
                  filteredSurveys.map((survey) => (
                    <SurveyCard key={survey?.assessmentID} survey={survey} />
                  ))
                ) : (
                  <p className="text-gray-500 col-span-full text-center">
                    No surveys available in this category.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
